import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Snackbar,
  Alert
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

const defaultTask = {
  id: null,
  title: '',
  description: '',
  status: 'todo',
  priority: 'medium',
  assignee: '',
  dueDate: ''
};

const priorityColors = {
  low: 'default',
  medium: 'warning',
  high: 'error'
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [current, setCurrent] = useState(defaultTask);
  const [filter, setFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const filteredTasks = useMemo(() => {
    if (filter === 'all') return tasks;
    return tasks.filter(t => t.status === filter);
  }, [tasks, filter]);

  const openNew = () => {
    setCurrent(defaultTask);
    setEditing(false);
    setDialogOpen(true);
  };

  const openEdit = (task) => {
    setCurrent(task);
    setEditing(true);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrent(prev => ({ ...prev, [name]: value }));
  };

  const saveTask = (e) => {
    e.preventDefault();
    if (!current.title.trim()) {
      setSnackbar({ open: true, message: 'Görev başlığı zorunludur', severity: 'error' });
      return;
    }
    if (editing) {
      setTasks(prev => prev.map(t => (t.id === current.id ? current : t)));
      setSnackbar({ open: true, message: 'Görev güncellendi', severity: 'success' });
    } else {
      const id = Date.now();
      setTasks(prev => [...prev, { ...current, id }]);
      setSnackbar({ open: true, message: 'Görev eklendi', severity: 'success' });
    }
    setDialogOpen(false);
  };

  const toggleDone = (task) => {
    const nextStatus = task.status === 'done' ? 'todo' : 'done';
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, status: nextStatus } : t)));
  };

  const removeTask = (id) => {
    if (!window.confirm('Bu görevi silmek istediğinize emin misiniz?')) return;
    setTasks(prev => prev.filter(t => t.id !== id));
    setSnackbar({ open: true, message: 'Görev silindi', severity: 'success' });
  };

  const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Görevler</Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small">
            <InputLabel>Filtre</InputLabel>
            <Select label="Filtre" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="todo">Yapılacak</MenuItem>
              <MenuItem value="in_progress">Devam Ediyor</MenuItem>
              <MenuItem value="done">Tamamlandı</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Yeni Görev</Button>
        </Stack>
      </Stack>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Durum</TableCell>
                <TableCell>Başlık</TableCell>
                <TableCell>Öncelik</TableCell>
                <TableCell>Ata</TableCell>
                <TableCell>Son Tarih</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">Henüz görev yok</TableCell>
                </TableRow>
              )}
              {filteredTasks.map(task => (
                <TableRow key={task.id} hover>
                  <TableCell>
                    <Checkbox checked={task.status === 'done'} onChange={() => toggleDone(task)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{task.title}</Typography>
                    {task.description && (
                      <Typography variant="body2" color="text.secondary">{task.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={task.priority} color={priorityColors[task.priority] || 'default'} />
                  </TableCell>
                  <TableCell>{task.assignee || '-'}</TableCell>
                  <TableCell>{task.dueDate || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEdit(task)} color="primary"><EditIcon /></IconButton>
                    <IconButton onClick={() => removeTask(task.id)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <form onSubmit={saveTask}>
          <DialogTitle>{editing ? 'Görevi Düzenle' : 'Yeni Görev'}</DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              required
              label="Başlık"
              name="title"
              value={current.title}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Açıklama"
              name="description"
              value={current.description}
              onChange={handleChange}
              multiline
              minRows={3}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select label="Durum" name="status" value={current.status} onChange={handleChange}>
                  <MenuItem value="todo">Yapılacak</MenuItem>
                  <MenuItem value="in_progress">Devam Ediyor</MenuItem>
                  <MenuItem value="done">Tamamlandı</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Öncelik</InputLabel>
                <Select label="Öncelik" name="priority" value={current.priority} onChange={handleChange}>
                  <MenuItem value="low">Düşük</MenuItem>
                  <MenuItem value="medium">Orta</MenuItem>
                  <MenuItem value="high">Yüksek</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
              <TextField fullWidth label="Atanan Kişi" name="assignee" value={current.assignee} onChange={handleChange} />
              <TextField fullWidth type="date" label="Son Tarih" name="dueDate" value={current.dueDate} onChange={handleChange} InputLabelProps={{ shrink: true }} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>İptal</Button>
            <Button type="submit" variant="contained">{editing ? 'Güncelle' : 'Ekle'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
